import type { CancelTokenSource } from 'axios';
import {
  useCallback, useEffect, useMemo, useRef, useState, useTransition,
} from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  convertMultipolygonToWktFormat, countMultipolygonVertices,
} from '~/_shared/types/polygon/polygon.utils';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  createReportSampleRadiusParams, getReportSampleData, isReportTypePolygon,
} from '~/exportReport/exportReport.helpers';
import {
  type AgsReportPolygonResponse, type AgsReportRingDataResponse, getAgsPolygonReportData, getAgsReportRingData,
} from '~/exportReport/exportReport.repository';
import {
  createAgsReportDataRingRequest, getReportDemographicDataFromResults, getReportDemographicLabelFromResults,
  type ReportDemographicData,
} from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import { getExportReportRadiusTableColumns } from '~/exportReport/useExportReportParams';
import {
  calculateAreaInSquaredMeters, getAreaText,
} from '~/map/map/distanceCalculator/calculateDistance';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import type { ReportTableColumn } from './table/ReportTable.model';

type UseExportReportDataParams = {
  isPreviewMode?: boolean;
  tableColumns: ReadonlyArray<ReportTableColumn>;
};

export const useExportReportData = ({ isPreviewMode = false, tableColumns }: UseExportReportDataParams) => {
  const [data, setData] = useState<AgsReportRingDataResponse | AgsReportPolygonResponse | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, startTransition] = useTransition();
  const [reportPolygon, setReportPolygon] = useState<MultiPolygon | null>(null);
  const exportReportState = useToolsStateExportReportSelector();
  const spreadsheetData = useLatLngSpreadsheetData();
  const cancelToken = useRef<null | CancelTokenSource>(null);
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const [t] = useTranslation();

  const isReportEmpty = useMemo(() => {
    // When there's no data, it means the report failed to load and as such cannot be determined as empty.
    if (isLoading || !data || !data.areas[0]) {
      return false;
    }

    return data.areas[0].data === null;
  }, [data, isLoading]);

  const sample = exportReportState.settings.sample;

  const center = useMemo(() => {
    if (!exportReportState.isActive) {
      return null;
    }

    // Sample reports always show radius data, regardless of their report type.
    if (sample) {
      return sample.center;
    }

    if (exportReportState.params.type === ExportReportType.Location) {
      const rowData = spreadsheetData.getRow(exportReportState.params.data.markerId);
      return rowData ?? null;
    }
    if (exportReportState.params.type === ExportReportType.Radius) {
      return exportReportState.params.data.center;
    }

    return null;
  }, [exportReportState, sample, spreadsheetData]);

  const displayedTableColumns = useMemo(() => {
    // Polygon columns can't be requested as rings, so polygon samples request the sample radius columns instead
    // A response with a single area is matched to any column, so the data still shows in the polygon column
    if (sample && isReportTypePolygon(sample.visualParams.type)) {
      return getExportReportRadiusTableColumns(createReportSampleRadiusParams(getReportSampleData(exportReportState.settings.country)), t);
    }

    return tableColumns;
  }, [sample, t, tableColumns, exportReportState.settings.country]);

  // set report polygon based on Boundary or Drive Time Polygon
  useEffect(() => {
    setReportPolygon(null);

    if (!exportReportState.isActive || sample) {
      return;
    }

    if (exportReportState.params.type === ExportReportType.Polygon || exportReportState.params.type === ExportReportType.DriveTimePolygon) {
      setReportPolygon(exportReportState.params.data.paths);
      return;
    }
  }, [exportReportState.isActive, exportReportState.params, sample]);

  const getDemographicData = useCallback((columnId: string, demographicId: string): ReportDemographicData | null => {
    if (isPreviewMode) {
      return null;
    }

    if (isLoading) {
      return {
        id: 'empty-value',
        value: '',
        label: '',
      };
    }

    return getReportDemographicDataFromResults(data?.areas ?? [], columnId, demographicId);
  }, [data?.areas, isLoading, isPreviewMode]);

  const getDemographicLabel = useCallback((demographicId: string): string | null => {
    if (isLoading) {
      return '';
    }

    return getReportDemographicLabelFromResults(data?.areas ?? [], demographicId);
  }, [data?.areas, isLoading]);

  // Location/Radius
  useEffect(() => {
    setIsError(false);

    if (!center || isPreviewMode || !clientId || !mapId) {
      return;
    }

    const reportDataRequest = {
      ...createAgsReportDataRingRequest({
        center,
        tableColumns: displayedTableColumns,
        varlistId: exportReportState.settings.template.varlistId,
        country: exportReportState.settings.country,
      }),
      mapId,
    };

    startTransition(async () => {
      try {
        if (reportDataRequest.radiiInMiles.length) {
          cancelToken.current = createCancelToken();
          const results = await getAgsReportRingData(clientId, reportDataRequest, cancelToken.current.token);

          setData(results.report);
        }
      }
      catch {
        setIsError(true);
      }
    });
  }, [center, isPreviewMode, displayedTableColumns, clientId, mapId, exportReportState.settings.template.varlistId, exportReportState.settings.country]);

  // Polygon: Boundary/Drive Time Polygon
  useEffect(() => {
    setIsError(false);

    if (!reportPolygon || isPreviewMode || !clientId || !mapId) {
      return;
    }

    const totalVertices = countMultipolygonVertices(reportPolygon);
    console.debug(`[ExportReport] Polygon vertices count: ${totalVertices}`);
    const polygonAreaInSquaredMeters = reportPolygon.reduce((acc, polygon) => acc + calculateAreaInSquaredMeters(polygon.path), 0);
    console.debug(`[ExportReport] Polygon area: ${getAreaText(polygonAreaInSquaredMeters, UnitSystem.imperial)})`);

    startTransition(async () => {
      try {
        const polygon = convertMultipolygonToWktFormat(reportPolygon);

        cancelToken.current = createCancelToken();
        const results = await getAgsPolygonReportData(clientId, {
          varlistId: exportReportState.settings.template.varlistId,
          country: exportReportState.settings.country,
          polygon,
          mapId,
        }, cancelToken.current.token);

        setData(results.report);
      }
      catch {
        setIsError(true);
      }
    });
  }, [reportPolygon, isPreviewMode, clientId, mapId, exportReportState.settings.template.varlistId, exportReportState.settings.country]);

  useEffect(() => {
    // Effects run after commit, so with data present and no pending transition the values are already rendered.
    if (!data || isLoading) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data, isLoading]);

  useEffect(() => {
    return () => {
      cancelToken.current?.cancel();
    };
  }, []);

  return {
    isLoading,
    isError,
    isReportEmpty,
    getDemographicData,
    getDemographicLabel,
  };
};
